import { useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import {
  ActionMeta,
  components,
  CSSObjectWithLabel,
  InputActionMeta,
  MultiValue,
  StylesConfig,
} from 'react-select';
import { isValidAAMutation, isValidABNotation, isValidNspNotation } from '../helpers/aa-mutation';
import { PangoCountSampleData } from '../data/sample/PangoCountSampleDataset';
import { isValidPangoLineageQuery, transformToVariantQuery, VariantSelector } from '../data/VariantSelector';
import { isValidNucMutation } from '../helpers/nuc-mutation';
import { useQuery } from '../helpers/query-hook';
import { useDeepCompareEffect } from '../helpers/deep-compare-hooks';
import Form from 'react-bootstrap/Form';
import { SamplingStrategy } from '../data/SamplingStrategy';

import { translateMutation } from '../helpers/autocomplete-helpers';
import { isValidAAInsertion } from '../helpers/aa-insertion';
import { isValidNucInsertion } from '../helpers/nuc-insertion';
import { _fetchAggSamples } from '../data/api-lapis';
import { useDrop } from 'react-dnd';
import { addDefaultHostAndQc } from '../data/HostAndQcSelector';
import { isValidUsherPhyloDescendant } from '../helpers/phylo';

type SearchType =
  | 'aa-mutation'
  | 'nuc-mutation'
  | 'aa-insertion'
  | 'nuc-insertion'
  | 'pango-lineage'
  | 'nextclade-pango-lineage'
  | 'nextstrain-clade'
  | 'usher-phylo-descendant';

type SearchOption = {
  label: string;
  value: string;
  type: SearchType;
};

const backgroundColor: { [key in SearchType]: string } = {
  'pango-lineage': 'rgba(29,78,207,0.1)',
  'nextclade-pango-lineage': 'rgba(29,78,207,0.1)',
  'nextstrain-clade': 'rgba(29,78,207,0.1)',
  'aa-mutation': 'rgba(4,133,27,0.1)',
  'nuc-mutation': 'rgba(33,162,162,0.29)',
  'aa-insertion': 'rgba(4,133,27,0.1)',
  'nuc-insertion': 'rgba(33,162,162,0.29)',
  'usher-phylo-descendant': 'rgba(183,45,197,0.29)',
};

function mapOption(optionString: string, type: SearchType): SearchOption {
  let value = optionString;
  let label = optionString;
  if (optionString.includes(' =')) {
    value = optionString.split(' ')[0];
  } else if (optionString.toLowerCase().startsWith('nsp')) {
    if (translateMutation(optionString.toLowerCase())) {
      value = translateMutation(optionString.toLowerCase());
      label = `${value} = (${optionString})`;
    }
  } else if (optionString.toLowerCase().startsWith('orf1ab')) {
    if (translateMutation(optionString.toLowerCase())) {
      value = translateMutation(optionString.toLowerCase());
      label = `${value} = (${optionString})`;
    }
  }
  if (type === 'nextclade-pango-lineage') {
    label = `${optionString} (Nextclade)`;
  }
  if (type === 'nextstrain-clade') {
    label = `${optionString} (Nextstrain clade)`;
  }
  if (type === 'usher-phylo-descendant') {
    label = `${optionString} (UShER tree)`;
  }
  return {
    label,
    value,
    type,
  };
}

function variantSelectorToOptions(selector: VariantSelector): SearchOption[] {
  const options: SearchOption[] = [];
  if (selector.pangoLineage) {
    options.push({ label: selector.pangoLineage, value: selector.pangoLineage, type: 'pango-lineage' });
  }
  if (selector.nextcladePangoLineage) {
    options.push({
      label: selector.nextcladePangoLineage + ' (Nextclade)',
      value: selector.nextcladePangoLineage,
      type: 'nextclade-pango-lineage',
    });
  }
  if (selector.nextstrainClade) {
    options.push({
      label: selector.nextstrainClade + ' (Nextstrain clade)',
      value: selector.nextstrainClade,
      type: 'nextstrain-clade',
    });
  }
  if (selector.aaMutations) {
    selector.aaMutations.forEach(m => options.push({ label: m, value: m, type: 'aa-mutation' }));
  }
  if (selector.nucMutations) {
    selector.nucMutations.forEach(m => options.push({ label: m, value: m, type: 'nuc-mutation' }));
  }
  if (selector.aaInsertions) {
    selector.aaInsertions.forEach(m => options.push({ label: m, value: m, type: 'aa-insertion' }));
  }
  if (selector.nucInsertions) {
    selector.nucInsertions.forEach(m => options.push({ label: m, value: m, type: 'nuc-insertion' }));
  }
  if (selector['usherTree.phyloDescendantOf']) {
    options.push({
      label: selector['usherTree.phyloDescendantOf'] + ' (UShER tree)',
      value: selector['usherTree.phyloDescendantOf'],
      type: 'usher-phylo-descendant',
    });
  }
  return options;
}

function optionsToVariantSelector(options: SearchOption[]): VariantSelector {
  const selector: VariantSelector = {
    aaMutations: [],
    nucMutations: [],
    aaInsertions: [],
    nucInsertions: [],
  };
  for (let { type, value } of options) {
    if (type === 'aa-mutation') {
      selector.aaMutations!.push(value);
    } else if (type === 'nuc-mutation') {
      selector.nucMutations!.push(value);
    } else if (type === 'aa-insertion') {
      selector.aaInsertions!.push(value);
    } else if (type === 'nuc-insertion') {
      selector.nucInsertions!.push(value);
    } else if (type === 'pango-lineage') {
      selector.pangoLineage = value.toUpperCase();
    } else if (type === 'nextclade-pango-lineage') {
      selector.nextcladePangoLineage = value.toUpperCase();
    } else if (type === 'nextstrain-clade') {
      selector.nextstrainClade = value;
    } else if (type === 'usher-phylo-descendant') {
      selector['usherTree.phyloDescendantOf'] = value;
    }
  }
  return selector;
}

const colorStyles: Partial<StylesConfig<any, true, any>> = {
  control: (styles: CSSObjectWithLabel) => ({ ...styles, backgroundColor: 'white' }),
  multiValue: (styles: CSSObjectWithLabel, { data }: { data: SearchOption }) => {
    return {
      ...styles,
      backgroundColor: backgroundColor[data.type],
    };
  },
};

type Props = {
  currentSelection?: VariantSelector;
  onVariantSelect: (selection: VariantSelector) => void;
  triggerSearch: () => void;
};

export const VariantSearchField = ({ onVariantSelect, currentSelection, triggerSearch }: Props) => {
  const [selectedOptions, setSelectedOptions] = useState<SearchOption[]>(
    currentSelection ? variantSelectorToOptions(currentSelection) : []
  );
  const [inputValue, setInputValue] = useState<string>('');
  const [variantQuery, setVariantQuery] = useState(currentSelection?.variantQuery ?? inputValue);
  const [advancedSearch, setAdvancedSearch] = useState(currentSelection?.variantQuery !== undefined);
  // dragEnter increases the depth by one and dragLeave decreases it. The two functions is being called every time when
  // the mouse enters or leaves the object or one of the children.

  const applySelector = (selector: VariantSelector) => {
    if (selector.variantQuery !== undefined) {
      setAdvancedSearch(true);
      setVariantQuery(selector.variantQuery);
    } else {
      setAdvancedSearch(false);
      setSelectedOptions(variantSelectorToOptions(selector));
    }
  };

  useDeepCompareEffect(() => {
    if (currentSelection) {
      applySelector(currentSelection);
    }
  }, [currentSelection]);

  function handleCheckboxChange() {
    let newQuery: string = '';
    if (selectedOptions.length > 0) {
      const selector = optionsToVariantSelector(selectedOptions);
      newQuery = transformToVariantQuery(selector);
    } else if (inputValue) {
      newQuery = inputValue;
    }
    setAdvancedSearch(!advancedSearch);
    setVariantQuery(newQuery);
  }

  // --- Drag&drop ---

  const changeAdvancedSearch = (value: boolean) => {
    setAdvancedSearch(value);
  };

  const dropToField = (query: any) => {
    if (query) {
      const selector = JSON.parse(query) as VariantSelector;
      if (selector.variantQuery) {
        changeAdvancedSearch(true);
        setSelectedOptions([]);
      } else {
        setVariantQuery('');
        changeAdvancedSearch(false);
      }

      applySelector(selector);
    }
  };

  const dragRef = useRef<HTMLDivElement>(null);
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'div',
    drop: (item: any) => dropToField(item.query),
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  }));
  drop(dragRef);

  // --- Rendering ---

  return (
    <div
      ref={dragRef}
      className={'m-1 p-1 border-2 border-dashed ' + (isOver ? 'border-black' : 'border-transparent')}
    >
      <form
        className='w-full flex flex-row items-center'
        onSubmit={e => {
          e.preventDefault();
          triggerSearch();
        }}
      >
        {!advancedSearch ? (
          <SimpleVariantSearchField
            selectedOptions={selectedOptions}
            setSelectedOptions={newSelectedOptions => {
              setSelectedOptions(newSelectedOptions);
              onVariantSelect(optionsToVariantSelector(newSelectedOptions));
            }}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        ) : (
          <AdvancedVariantSearchField
            variantQuery={variantQuery}
            setVariantQuery={(newValue: string) => {
              setVariantQuery(newValue);
              onVariantSelect({ variantQuery: newValue });
            }}
          />
        )}
      </form>
      <div>
        <Form.Check
          type='checkbox'
          label='Advanced search'
          checked={advancedSearch}
          onChange={_ => handleCheckboxChange()}
          className='mb-3'
        />
      </div>
    </div>
  );
};

type SimpleVariantSearchFieldProps = {
  selectedOptions: SearchOption[];
  setSelectedOptions: (newOptions: SearchOption[]) => void;
  inputValue: string;
  setInputValue: (newValue: string) => void;
};

function SimpleVariantSearchField({
  selectedOptions,
  setSelectedOptions,
  inputValue,
  setInputValue,
}: SimpleVariantSearchFieldProps) {
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);

  const pangoLineages = useQuery(
    signal =>
      PangoCountSampleData.fromApi(
        addDefaultHostAndQc({ location: {}, samplingStrategy: SamplingStrategy.AllSamples }),
        signal
      ).then(dataset => dataset.payload.filter(e => e.pangoLineage).map(e => e.pangoLineage!)),
    []
  );

  const nextstrainCladesSet = useQuery(
    signal =>
      _fetchAggSamples(
        addDefaultHostAndQc({ location: {}, samplingStrategy: SamplingStrategy.AllSamples }),
        ['nextstrainClade'],
        signal
      ).then(dataset => new Set(dataset.filter(e => e.nextstrainClade).map(e => e.nextstrainClade!))),
    []
  );

  const suggestPangolinLineages = (query: string): string[] => {
    return (pangoLineages.data ?? []).filter(pl => pl.toUpperCase().startsWith(query.toUpperCase()));
  };

  const suggestOptions = (query: string): SearchOption[] => {
    const onePangoLineageAlreadySelected =
      selectedOptions.filter(
        option => option.type === 'pango-lineage' || option.type === 'nextclade-pango-lineage'
      ).length > 0;
    const oneNextstrainCladeAlreadySelected =
      selectedOptions.filter(option => option.type === 'nextstrain-clade').length > 0;
    const oneUsherPhyloDescendantAlreadySelected =
      selectedOptions.filter(option => option.type === 'usher-phylo-descendant').length > 0;
    const suggestions: SearchOption[] = [];

    const queryWithoutNextcladeLabel = query.replace('(Nextclade)', '').trim();
    if (!onePangoLineageAlreadySelected && isValidPangoLineageQuery(queryWithoutNextcladeLabel)) {
      suggestions.push(mapOption(queryWithoutNextcladeLabel, 'nextclade-pango-lineage'));
      if (!query.includes('*')) {
        suggestions.push(mapOption(queryWithoutNextcladeLabel + '*', 'nextclade-pango-lineage'));
      }
    }
    if (isValidAAMutation(query)) {
      suggestions.push(mapOption(query, 'aa-mutation'));
    } else if (isValidABNotation(query)) {
      suggestions.push(mapOption(query, 'aa-mutation'));
    } else if (isValidNucMutation(query)) {
      suggestions.push(mapOption(query, 'nuc-mutation'));
    } else if (isValidAAInsertion(query)) {
      suggestions.push(mapOption(query, 'aa-insertion'));
    } else if (isValidNucInsertion(query)) {
      suggestions.push(mapOption(query, 'nuc-insertion'));
    } else if (!onePangoLineageAlreadySelected && isValidPangoLineageQuery(query)) {
      suggestions.push(mapOption(query, 'pango-lineage'));
      if (!query.endsWith('*')) {
        suggestions.push(mapOption(query + '*', 'pango-lineage'));
      }
    } else if (!oneUsherPhyloDescendantAlreadySelected && isValidUsherPhyloDescendant(query)) {
      suggestions.push(mapOption(query, 'usher-phylo-descendant'));
    }
    const queryWithoutNextstrainLabel = query.replace('(Nextstrain clade)', '').trim();
    if (
      !oneNextstrainCladeAlreadySelected &&
      nextstrainCladesSet.data?.has(queryWithoutNextstrainLabel.toUpperCase())
    ) {
      suggestions.push(mapOption(queryWithoutNextstrainLabel, 'nextstrain-clade'));
    }
    if (!onePangoLineageAlreadySelected) {
      suggestions.push(...suggestPangolinLineages(query).map(pl => mapOption(pl, 'pango-lineage')));
    }
    return suggestions.slice(0, 20);
  };

  // nsp + ':' + letterBefore + nspCodon; //
  /**
   * Handles the input change:
   * 1) when input value contains "," or "+", call the handleCommaSeparatedInput function
   * 2) otherwise, set the input value to the new value and keep menu window open
   * 3) when input change action is menu-close or input-blur, close menu window
   * @param newValue the new input value
   * @param change the change action
   */
  const handleInputChange = (newValue: string, change: InputActionMeta) => {
    if (change.action === 'input-change') {
      // when input value has "," or "+" in the string
      if (newValue.includes(',') || newValue.includes('+')) {
        handleCommaSeparatedInput(newValue);
      } else {
        setInputValue(newValue);
        setMenuIsOpen(true);
      }
    }

    if (change.action === 'menu-close' || change.action === 'input-blur') {
      setMenuIsOpen(false);
    }
  };

  /**
   * Handles comma-separated input value:
   * 1) split the input value by "," and "+" to retrieve the individual query in the list
   * 2) validate each input query by mapping to suggest options
   * 3) add valid options to the selected options list so they transform from plain text to tags
   * 4) max 1 pango lineage but multiple mutations allowed
   * 5) invalid queries stay as comma-separated plain text
   * 6) leave options menu open if there are invalid queries from the list
   * @param inputValue comma-separated string
   */
  const handleCommaSeparatedInput = (inputValue: string) => {
    // const input = inputValue.split(/[,+]/);
    const inputValues = inputValue.split(/[,+]/).map(value => {
      if (isValidABNotation(value)) {
        return translateMutation(value);
      } else if (isValidNspNotation(value)) {
        return translateMutation(value);
      } else {
        return value;
      }
    });
    let newSelectedOptions: SearchOption[] = [];
    let invalidQueries = '';
    for (let query of inputValues) {
      query = query.trim();
      const suggestions = suggestOptions(query);
      const selectedOption = suggestions.find(
        option =>
          option.value.toUpperCase() === query.toUpperCase() ||
          option.label.toUpperCase() === query.toUpperCase()
      );
      if (
        suggestions &&
        suggestions.length > 0 &&
        selectedOption &&
        !selectedOptions.find(option => option.value === selectedOption.value)
      ) {
        if (
          selectedOption.type === 'aa-mutation' ||
          selectedOption.type === 'nuc-mutation' ||
          selectedOption.type === 'aa-insertion' ||
          selectedOption.type === 'nuc-insertion' ||
          ((selectedOption.type === 'pango-lineage' || selectedOption.type === 'nextclade-pango-lineage') &&
            newSelectedOptions.filter(
              option => option.type === 'pango-lineage' || option.type === 'nextclade-pango-lineage'
            ).length < 1) ||
          (selectedOption.type === 'nextstrain-clade' &&
            newSelectedOptions.filter(option => option.type === 'nextstrain-clade').length < 1) ||
          (selectedOption.type === 'usher-phylo-descendant' &&
            newSelectedOptions.filter(option => option.type === 'usher-phylo-descendant').length < 1)
        ) {
          newSelectedOptions.push(selectedOption);
        }
      } else {
        invalidQueries += query + ',';
      }
    }

    if (newSelectedOptions.length > 0) {
      setSelectedOptions([...selectedOptions, ...newSelectedOptions]);
    }

    // remove the last "," in the invalidQueries string
    invalidQueries = invalidQueries.slice(0, -1);
    setInputValue(invalidQueries);
    setMenuIsOpen(invalidQueries !== '');
  };

  const promiseOptions = (inputValue: string) => {
    return Promise.resolve(suggestOptions(inputValue));
  };

  const openMenu = () => {
    setMenuIsOpen(!menuIsOpen);
  };

  /**
   * This is needed because the code is converted to manually opening/closing the option menu,
   * which overrides the default behaviour of the DropdownIndicator.
   * @param props props of component
   * @constructor the constructor
   */
  const DropdownIndicator = (props: any) => {
    return (
      components.DropdownIndicator && (
        <div onClick={openMenu} style={{ display: 'flex', alignItems: 'center' }}>
          <components.DropdownIndicator {...props} onClick={() => {}} children={props.children} />
        </div>
      )
    );
  };

  return (
    <AsyncSelect
      className='w-full mr-2'
      components={{ DropdownIndicator }}
      placeholder='ins_S:214:EPE, ins_22204:?GAG?GAA?, B.1.1.7, S:484K, C913'
      isMulti
      defaultOptions={suggestOptions('')}
      loadOptions={promiseOptions}
      onChange={(_: MultiValue<SearchOption>, change: ActionMeta<SearchOption>) => {
        if (change.action === 'select-option') {
          setSelectedOptions([...selectedOptions!, change.option!]);
          setInputValue('');
          setMenuIsOpen(false);
        } else if (change.action === 'remove-value' || change.action === 'pop-value') {
          setSelectedOptions(selectedOptions.filter(o => o.value !== change.removedValue.value));
        } else if (change.action === 'clear') {
          setSelectedOptions([]);
          setInputValue('');
        }
      }}
      styles={colorStyles}
      onInputChange={handleInputChange}
      value={selectedOptions}
      inputValue={inputValue}
      menuIsOpen={menuIsOpen}
    />
  );
}

type AdvancedSearchFieldProps = {
  variantQuery: string;
  setVariantQuery: (newValue: string) => void;
};

function AdvancedVariantSearchField({ variantQuery, setVariantQuery }: AdvancedSearchFieldProps) {
  return (
    <Form.Control
      type='text'
      placeholder='(B.1.1.529* | S:67V) & !C913T'
      className='w-full mr-2'
      value={variantQuery}
      onChange={e => setVariantQuery(e.target.value)}
    />
  );
}
